import * as React from "react";
import { styled } from "@mui/material";
import { DocQuestion } from "../../DocQuestion";
import clsx from "clsx";
import { PatientColorMap } from "../../../../layout/patientColorMap";
import { GenericButton } from "../../../uiControllers/genericButton";
import { useTranslate } from "../../../../hooks/useTranslate";
import { forwardRef } from "react";

//region [[ Styles ]]

const RangeQuestionLayoutView = styled("div")({});

const QuestionContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});

const RangeInputArea = styled("div")({
  marginTop: "15px",
  display: "flex",
  flexDirection: "column",
  padding: "22px",
  background: "rgb(247, 247, 249)", //PatientColorMap.DefaultLight,
  borderRadius: "30px",
});

const RangeContent = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
});

const BottomIcons = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
});

const SubmitButton = styled(GenericButton)({
  "&.generic-button, &.generic-button:hover, &.generic-button:focus": {
    height: "33px",
    minWidth: "88px",
  },
});

const SkipButton = styled(SubmitButton)({
  "&.generic-button": {
    backgroundColor: PatientColorMap.PrimaryLight,
    color: PatientColorMap.Primary,
    "&:hover": {
      backgroundColor: PatientColorMap.PrimaryLight,
    },
  },
});

const TextAsTitle = styled("div")({
  margin: "5px",
  marginBottom: "15px",
  alignItems: "center",
  fontSize: "14px",
  fontWeight: 400,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface RangeQuestionLayoutProps {
  isAnswered: boolean;
  onSubmitClick?: () => void;
  questionText: string;
  className?: string;
  onSkipClicked?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  submitDisabled?: boolean;
  textAsQuestion?: boolean;
  // Use this if you want to hide the submit button
  // (we currently hack this for the medication frequency question,
  // but also in forms where we just submit on user change)
  preventSubmit?: boolean;
  preventSkip?: boolean;
  miniView?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const NumericQuestionLayout = React.memo(
  forwardRef<HTMLDivElement, RangeQuestionLayoutProps>(
    (
      { isAnswered, textAsQuestion = true, preventSubmit = false, ...props },
      ref,
    ) => {
      const { translate } = useTranslate();

      return (
        <RangeQuestionLayoutView
          className={clsx(
            props.className,
            "range-question-layout",
            props.miniView ? "mini-view" : "",
          )}
          ref={ref}
        >
          {textAsQuestion && (
            <QuestionContainer className={"question-container"}>
              <DocQuestion
                text={props.questionText}
                miniView={props.miniView}
              />
            </QuestionContainer>
          )}
          {!isAnswered && (
            <RangeInputArea className={"range-input-area"}>
              {!textAsQuestion && (
                <TextAsTitle>{props.questionText}</TextAsTitle>
              )}
              <RangeContent className={"range-content"}>
                {props.children}
              </RangeContent>
              <BottomIcons>
                {!props.preventSkip && !props.miniView && (
                  <SkipButton
                    label={translate("SKIP")}
                    onClick={props.onSkipClicked}
                  />
                )}
                {!preventSubmit && !props.miniView && (
                  <SubmitButton
                    label={translate("SUBMIT")}
                    onClick={props.onSubmitClick}
                    disabled={props.submitDisabled}
                  />
                )}
              </BottomIcons>
            </RangeInputArea>
          )}
        </RangeQuestionLayoutView>
      );
    },
  ),
);
